// LAP-646 — Card "Répartition des interactions" : donut famille / amis / pro / amour / autres
// LAP-XXX — Social Dashboard v2 : passage aux 4 buckets canoniques
//          (family / pro / friendship / intimacy), couleurs design tokens.
import React from 'react'
import { Box, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Doughnut } from 'react-chartjs-2'
import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined'
import CardHeader from '../../Investments/_shared/CardHeader'
import EmptyHelp from './EmptyHelp'
import { RELATIONSHIP_COLORS } from '../../MesContacts/_shared/RelationshipFields'

// LAP-XXX — alignement design tokens (cf. plan §5).
const BUCKET_PALETTE = {
  ...RELATIONSHIP_COLORS,
  // Compat ascendante au cas où l'API renverrait encore d'anciens labels.
  famille: RELATIONSHIP_COLORS.family,
  amis: RELATIONSHIP_COLORS.friendship,
  amour: RELATIONSHIP_COLORS.intimacy,
  autres: '#94a3b8',
}

export default function RepartitionCard({ data, loading }) {
  const { t } = useTranslation('layout')
  const items = Array.isArray(data) ? data : []
  const nonEmpty = items.filter((i) => i.value > 0)

  if (!loading && nonEmpty.length === 0) {
    return (
      <Box display="flex" flexDirection="column" height="100%">
        <CardHeader
          icon={<PieChartOutlineOutlinedIcon fontSize="small" />}
          title={t('social.repartition.title', 'Répartition des interactions')}
          subtitle={t('social.repartition.subtitle', '3 derniers mois')}
        />
        <EmptyHelp
          icon={<PieChartOutlineOutlinedIcon sx={{ fontSize: 22 }} />}
          title={t('social.empty.repartition.title', 'Aucune interaction à répartir')}
          description={t(
            'social.empty.repartition.description',
            'Catégorise tes contacts (famille, amis, pro, amour…) puis lie-les à des événements de ton calendrier. La répartition s’affichera dès qu’une interaction sera enregistrée.',
          )}
          actions={[
            { key: 'contacts', label: t('social.empty.cta.addContact', 'Ajouter un contact') },
            { key: 'calendar', label: t('social.empty.cta.createEvent', 'Créer un événement') },
          ]}
        />
      </Box>
    )
  }

  // Toujours afficher les 4 buckets dans la légende, même à 0.
  const labels = items.map((i) =>
    t(`social.contact.relationshipType.${i.label}`, i.label),
  )
  const values = items.map((i) => i.value)
  const colors = items.map((i) => BUCKET_PALETTE[i.label] || '#94a3b8')
  const total = values.reduce((s, v) => s + v, 0)

  // On ne dessine le donut que sur les segments > 0 pour ne pas avoir un
  // anneau plein de couleurs invisibles.
  const drawnValues = items.map((i) => (i.value > 0 ? i.value : 0))
  const chartData = {
    labels,
    datasets: [
      {
        data: drawnValues,
        backgroundColor: colors,
        borderColor: 'rgba(0,0,0,0)',
        borderWidth: 2,
        hoverOffset: 6,
      },
    ],
  }
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      datalabels: { display: false },
      legend: { display: false },
    },
  }

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <CardHeader
        icon={<PieChartOutlineOutlinedIcon fontSize="small" />}
        title={t('social.repartition.title', 'Répartition des interactions')}
        subtitle={t('social.repartition.subtitle', '3 derniers mois')}
        value={total > 0 ? `${total}` : null}
      />
      <Box display="flex" alignItems="center" mt={2} sx={{ flexGrow: 1, minHeight: 0 }} gap={2}>
        <Box sx={{ width: 140, height: 140, position: 'relative', flexShrink: 0 }}>
          <Doughnut data={chartData} options={options} />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box sx={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{total}</Box>
            <Box sx={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('social.repartition.legendTotal', 'Interactions')}
            </Box>
          </Box>
        </Box>
        <Stack spacing={0.75} sx={{ flexGrow: 1, minWidth: 0 }}>
          {items.map((i, idx) => {
            const pct = total > 0 ? Math.round((i.value / total) * 100) : 0
            const label = t(`social.contact.relationshipType.${i.label}`, i.label)
            return (
              <Box key={idx} display="flex" alignItems="center" sx={{ fontSize: 13 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: BUCKET_PALETTE[i.label] || '#94a3b8',
                    mr: 1,
                  }}
                />
                <Box sx={{ color: 'rgba(255,255,255,0.85)' }}>{label}</Box>
                <Box sx={{ ml: 'auto', fontWeight: 600, color: '#fff' }}>
                  {i.value}
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.5)', ml: 0.5, fontSize: 11 }}>
                    ({pct}%)
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Stack>
      </Box>
    </Box>
  )
}
